/**
 * Error messages for errors that can happen when calling the Cognito API.
 * Please see
 * {@link https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference|Cognito API}
 * for reference.
 *
 * Keyed off `error.name` (v6 named `AuthError` subclasses, e.g.
 * `NotAuthorizedException`) — v5 keyed the same table off `error.code`
 * instead, but the string values are unchanged.
 */

const COGNITO_API_ERROR_MSGS = {
  // If the confirmation code is invalid.
  CodeMismatchException: 'cognitoAPI_error_invalid_verification',
  // If the confirmation code expired.
  ExpiredCodeException: 'cognitoAPI_error_code_expired',
  // When the same action is tried too many times.
  LimitExceededException: 'cognitoAPI_error_attempt_limit_exceeded',
  // When a user isn't found.
  UserNotFoundException: 'cognitoAPI_error_could_not_find_a_user',
  NotAuthorizedException: 'cognitoAPI_error_user_or_password_invalid',
  // Default error message if none of the above matches.
  default: 'cognitoAPI_error_an_unexpected_error',
};

interface IAuth {
  global?: boolean;
}

/**
 * The subset of `aws-amplify/auth` standalone functions this factory wraps.
 * Typed directly off the real module so the wrapper's internal signatures
 * always track the installed `aws-amplify` version.
 */
type AuthModule = Pick<
  typeof import('aws-amplify/auth'),
  | 'signIn'
  | 'confirmSignIn'
  | 'fetchAuthSession'
  | 'getCurrentUser'
  | 'fetchUserAttributes'
  | 'signOut'
  | 'resetPassword'
  | 'confirmResetPassword'
  | 'setUpTOTP'
  | 'verifyTOTPSetup'
  | 'updatePassword'
  | 'fetchDevices'
  | 'rememberDevice'
  | 'forgetDevice'
>;

interface Config {
  Auth: AuthModule;
  i18n: {
    t: (string) => string;
  };
}

/**
 * Wraps a single `aws-amplify/auth` call: if it throws, translate the error
 * message via `COGNITO_API_ERROR_MSGS` (keyed off `error.name`) and rethrow
 * with added `isError` and `userMessage` flags, preserving all original
 * error properties. `userMessage` lets `getUserFacingErrorMessage` render
 * the translated copy while every other error stays hidden from the user.
 */
const wrapAuthCall = <Args extends unknown[], Result>(
  fn: (...args: Args) => Promise<Result>,
  i18n: Config['i18n'],
) => {
  return async (...args: Args): Promise<Result> => {
    try {
      return await fn(...args);
    } catch (error: any) {
      const translatedMessage =
        i18n.t(COGNITO_API_ERROR_MSGS[error?.name]) ||
        i18n.t(COGNITO_API_ERROR_MSGS.default);

      throw {
        ...error,
        isError: true,
        message: translatedMessage,
        // Marks the translated copy as safe to render (see getUserFacingErrorMessage).
        userMessage: translatedMessage,
      };
    }
  };
};

/**
 * Wraps `aws-amplify/auth` standalone v6 functions, exposing the SAME public
 * method names the v5 Proxy-based wrapper exposed. If an error is thrown from
 * one of the calls we check if a translation exists for the error name and
 * update the error message with the translation, otherwise we update the
 * error message with a default message; then we rethrow the error.
 *
 * We also add an `isError` property to the error object to be able to easily
 * pass this error object around and check if it's an error anywhere in code.
 *
 * Unlike the v5 wrapper (a `Proxy` over the `Auth` singleton), v6 has no
 * single object to intercept — its functions are standalone tree-shakeable
 * module exports — so this builds a plain object of wrapped functions via
 * `Object.fromEntries` instead.
 */
export const authServiceFactory = ({ Auth, i18n }: Config) => {
  const methods = {
    signIn: (email: string, password: string) =>
      Auth.signIn({ username: email, password }),
    confirmSignIn: (challengeResponse: string) =>
      Auth.confirmSignIn({ challengeResponse }),
    // v6 handles the temporary-password challenge through the same
    // `confirmSignIn` call as any other sign-in challenge. `userAttributes`
    // (optional, e.g. `{ name: email }`) restores v5's 3-arg
    // `completeNewPassword(user, newPassword, requiredAttributeData)`
    // behavior of setting a Cognito attribute during the
    // NEW_PASSWORD_REQUIRED challenge — verified against the installed v6
    // source (`ConfirmSignInOptions.userAttributes`, threaded into
    // `handleCompleteNewPasswordChallenge`'s `requiredAttributes`).
    completeNewPassword: (
      newPassword: string,
      userAttributes?: Record<string, string>,
    ) =>
      Auth.confirmSignIn({
        challengeResponse: newPassword,
        ...(userAttributes ? { options: { userAttributes } } : {}),
      }),
    currentSession: () => Auth.fetchAuthSession(),
    // v5 exposed one call (`currentAuthenticatedUser`) that returned both the
    // user and its attributes; v6 splits that into two calls, merged here.
    currentAuthenticatedUser: async () => {
      const [user, attributes] = await Promise.all([
        Auth.getCurrentUser(),
        Auth.fetchUserAttributes(),
      ]);

      return { ...user, attributes };
    },
    signOut: ({ global }: IAuth) => Auth.signOut({ global }),
    forgotPassword: (email: string) => Auth.resetPassword({ username: email }),
    forgotPasswordSubmit: (
      userEmail: string,
      code: string,
      newPassword: string,
    ) =>
      Auth.confirmResetPassword({
        username: userEmail,
        confirmationCode: code,
        newPassword,
      }),
    setupTOTP: () => Auth.setUpTOTP(),
    verifyTotpToken: (authCode: string) =>
      Auth.verifyTOTPSetup({ code: authCode }),
    changePassword: (oldPassword: string, newPassword: string) =>
      Auth.updatePassword({ oldPassword, newPassword }),
    fetchDevices: () => Auth.fetchDevices(),
    rememberDevice: () => Auth.rememberDevice(),
    forgetDevice: () => Auth.forgetDevice(),
  };

  return Object.fromEntries(
    Object.entries(methods).map(([name, fn]) => [
      name,
      wrapAuthCall(fn as (...args: unknown[]) => Promise<unknown>, i18n),
    ]),
  ) as {
    [K in keyof typeof methods]: (
      ...args: Parameters<(typeof methods)[K]>
    ) => ReturnType<(typeof methods)[K]>;
  };
};
